import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import Order, { OrderType } from "../../services/Order";
import { useTypedSelector } from "../../redux/store";
import {
	getOrderPassengerPoints,
	stringifyPoint,
} from "../../redux/constants/OrdersPage/order";
import {
	sortByNumberOrString,
	isTextMatchingLeadingStarsFilterHelper,
	isTextMatchingAnySegmentFilterHelper,
} from "../../utils";
import { ColumnId } from "../../components/OrderPageWidgets/OrderTable/components/ModelTable/columns";
import { MessageTemplateActions } from "../../types/MessageTemplateEnums";
import { sameOrderItems } from "../../utils/ordersHotPath";
import { getOrderPaymentStatus } from "../../utils/orderPaymentStatus";

import { isLimit, isOrder, isQuery } from ".";

const useOrderSubHandler = (): useOrderSubHandler.Props => {
	const prevSortRef = useRef<{
		dataKey?: string;
		sortType?: any;
		input?: Order.Model[];
		sorted?: Order.Model[];
		ids?: number[];
		values?: unknown[];
	}>();

	const { t } = useTranslation();
	const language = useTypedSelector((state) => state.session.language);

	const getGroupStatic = useMemo(() => {
		const safeDate = (
			value?: string | number | Date | null,
			fallback = 0,
		) => {
			const ts = value ? new Date(value).getTime() : NaN;
			return Number.isFinite(ts) ? ts : fallback;
		};

		return {
			getOrderDate: (item: Order.Model) =>
				item?.isPreliminary && item?.orderDate
					? safeDate(item.orderDate, 1)
					: 1,
			getExecutorArrivalTime: (item: Order.Model) =>
				item?.executorToOrder?.[0]?.additionalFields?.arrivalTime ?? 1,
			getTimeToDelivery: (item: Order.Model) => {
				const executorArrivalTimestamp =
					item?.executorToOrder?.[0]?.additionalFields?.arrivalTime ??
					1;
				const time = item?.orderDate || item?.filledAt;
				const orderTime = safeDate(time, 1);
				return executorArrivalTimestamp || orderTime;
			},
			getExecutorCallSign: (order: Order.Model) => {
				const executor = order?.executorToOrder?.length
					? order.executorToOrder[0]?.executor
					: order?.offer?.executor;
				return executor?.callSign ?? "1";
			},
			getPickupSettlement: (item: Order.Model) =>
				getOrderPassengerPoints(item)?.[0]?.settlement ?? "",
			getPhone: (item: Order.Model) => item.phones?.at(-1)?.number ?? "1",
			getOrderPrice: (item: Order.Model) => item?.cost?.value ?? 0,
			getCustomerCallStatus: (item: Order.Model) =>
				item?.customerCallStatus?.status ?? "1",
			getExecutorCallStatus: (item: Order.Model) =>
				item?.executorCallStatus?.status ?? "1",
			getOrderNumber: (item: Order.Model) => item?.orderNumber ?? 1,
			getExecutingStageWithClientsTime: (item: Order.Model) =>
				item?.executingStages?.with_clients?.timestamp ?? "1",
			getStatus: (item: Order.Model) => item.status ?? "",
			getSource: (item: Order.Model) => item.source ?? "",
			getSourceCallSing: (item: Order.Model) =>
				item.additionalFields?.initiator?.callSign ?? "",
			getExecutingStage: (item: Order.Model) => item.executingStage ?? "",
			getDestinationSettlement: (item: Order.Model) =>
				getOrderPassengerPoints(item)?.at(-1)?.settlement ?? "",
			getCloseReason: (item: Order.Model) => {
				const data = item.closedOrderComments?.[0];
				const status = data?.status ?? "";
				let value = "";

				if (data?.dispatcher)
					value = data.dispatcher?.callSign
						? `dispatcher_${data.dispatcher.callSign}`
						: "";
				else if (data?.executor)
					value = data.executor?.callSign
						? `executor_${data.executor.callSign}`
						: "";
				else if (data?.customer) value = "client";

				return `${value}${status}`.trim();
			},
			getPaymentType: (item: Order.Model) => item.paymentType ?? "1",
			getPaymentStatus: (item: Order.Model) =>
				getOrderPaymentStatus(item) ?? "",
			getTotalRideCount: (item: Order.Model) =>
				item?.passengers?.[0]?.customer?.counters?.total ?? 0,
			getCreateOrderDate: (item: Order.Model) =>
				safeDate(item.createdAt, 0),
			getCreateOrderTime: (item: Order.Model) =>
				safeDate(item.createdAt, 0),
			getCustomerFullName: (item: Order.Model) => {
				const person =
					item.type === OrderType.CUSTOMER_SPONSORED
						? item.billingCustomer?.person
						: item.passengers?.[0]?.customer?.person;

				const name = person?.name ?? "";
				const surname = person?.surname ?? "";
				const fatherName = person?.fatherName ?? "";
				const fullName = `${surname} ${name} ${fatherName}`.trim();

				return `person_${fullName || "notfound"}`;
			},
			getCounterparty: (item: Order.Model) =>
				item?.counterparty?.additionalFields?.name ?? "",
			getCounterpartyCheck: (item: Order.Model) =>
				item?.checkCard?.check?.additionalFields?.checkValue ?? "",
			getCounterpartyCheckCard: (item: Order.Model) =>
				item?.checkCard?.code?.value ?? "",
			getExecutorLate: (item: Order.Model) =>
				item?.executingStages?.delayed?.data?.delay ?? "",
			getExecutingStageAcceptedTime: (item: Order.Model) =>
				item.executingStages?.accepted?.timestamp ?? 0,
			getExecutingStageArrivedTime: (item: Order.Model) =>
				item.executingStages?.arrived?.timestamp ?? "",
			getExecutingStageWaitingCustomerTime: (item: Order.Model) =>
				item.executingStages?.waiting_client?.timestamp ?? "",
			getIdleTime: (item: Order.Model) =>
				item?.additionalFields?.idleTimeMilliseconds ?? 0,
			getDiscount: (item: Order.Model) =>
				`${item.additionalFields?.discount?.active ?? ""}`,
			getRideNumber: (item: Order.Model) =>
				item.passengers?.at(0)?.rideNumber ?? 1,
		};
	}, []);

	const getGroupTranslation = useMemo(() => {
		const translationGroup = {
			pointOnMap: t(["point_on_map"]) || "",
			client: t("client") || "",
		};

		return {
			getPickupAddress: (item: Order.Model) => {
				const points = getOrderPassengerPoints(item) ?? [];
				return points[0]
					? stringifyPoint(
							points[0],
							"address",
							translationGroup.pointOnMap,
					  )
					: "";
			},
			getDestinationAddress: (item: Order.Model) => {
				const point = getOrderPassengerPoints(item)?.at(-1);
				return point
					? stringifyPoint(
							point,
							"address",
							translationGroup.pointOnMap,
					  )
					: "";
			},
			getDispatcherCallSign: (item: Order.Model) => {
				const data = item.closedOrderComments?.[0];
				if (data?.dispatcher)
					return data.dispatcher?.callSign
						? `dispatcher_${data.dispatcher.callSign ?? "1"}`
						: "dispatcher";
				if (data?.executor)
					return data.executor?.callSign
						? `executor_${data.executor.callSign ?? "1"}`
						: "executor";
				if (data?.customer)
					return translationGroup.client ?? "customer";
				return "1";
			},
			getSMSLateStatus: (item: Order.Model) =>
				t(
					`order_sms_message_statuses.${
						item.smsMessages?.find(
							(smsMessage) =>
								smsMessage?.action?.toString() ===
								MessageTemplateActions.LATE.toString(),
						)?.status
					}`,
				) ?? "",
			getSMSWillBeAtStatus: (item: Order.Model) =>
				t(
					`order_sms_message_statuses.${
						item.smsMessages?.find(
							(smsMessage) =>
								smsMessage?.action?.toString() ===
								MessageTemplateActions.WILL_BE_TIME.toString(),
						)?.status
					}`,
				) ?? "",
			getSMSCarNotFoundStatus: (item: Order.Model) =>
				t(
					`order_sms_message_statuses.${
						item.smsMessages?.find(
							(smsMessage) =>
								smsMessage?.action?.toString() ===
								MessageTemplateActions.CAR_NOT_FOUND.toString(),
						)?.status
					}`,
				) ?? "",
			getSMSAwaitingPassengersStatus: (item: Order.Model) =>
				t(
					`order_sms_message_statuses.${
						item.smsMessages?.find(
							(smsMessage) =>
								smsMessage?.action?.toString() ===
								MessageTemplateActions.OUTPUT_PASSENGERS.toString(),
						)?.status
					}`,
				) ?? "",
			getSMSWithPassengersStatus: (item: Order.Model) =>
				t(
					`order_sms_message_statuses.${
						item.smsMessages?.find(
							(smsMessage) =>
								smsMessage?.action?.toString() ===
								MessageTemplateActions.WITH_PASSENGERS.toString(),
						)?.status
					}`,
				) ?? "",
			getSMSOrderCompletedStatus: (item: Order.Model) =>
				t(
					`order_sms_message_statuses.${
						item.smsMessages?.find(
							(smsMessage) =>
								smsMessage?.action?.toString() ===
								MessageTemplateActions.ORDER_SUCCESSFULLY_COMPLETED.toString(),
						)?.status
					}`,
				) ?? "",
			getSMSExecutorSwitchedStatus: (item: Order.Model) =>
				t(
					`order_sms_message_statuses.${
						item.smsMessages?.find(
							(smsMessage) =>
								smsMessage?.action?.toString() ===
								MessageTemplateActions.DRIVER_SWITCHED.toString(),
						)?.status
					}`,
				) ?? "",
			getParameters: (item: Order.Model) => {
				const rateSettings = item.additionalFields?.rateSettings ?? {};
				return Object.keys(rateSettings)
					.filter((type) => rateSettings?.[type])
					.map((type) => t(`order_rate_setting.${type}`) ?? "")
					.join(", ");
			},
			getTransportationType: (item: Order.Model) => {
				const type = item.additionalFields?.transportationType;
				return (
					t([
						`orderPage.order_form.transportation_types.${type}`,
						type,
					]) ?? ""
				);
			},
		};
	}, [t]);

	const getGroupLanguage = useMemo(
		() => ({
			getPickupSector: (item: Order.Model) =>
				getOrderPassengerPoints(item)?.[0]?.sector?.name?.[language] ??
				"",

			getCarClass: (item: Order.Model) =>
				item.orderToCarClasses
					?.map((it) => it?.carClass?.name?.[language] ?? "")
					.filter(Boolean)
					.join(", ") ?? "",
			getDestinationSector: (item: Order.Model) =>
				getOrderPassengerPoints(item)?.at(-1)?.sector?.name?.[
					language
				] ?? "",
			getCompany: (item: Order.Model) =>
				item.taxiService?.company?.name?.[language] ?? "",
			getTaxiService: (item: Order.Model) =>
				item.taxiService?.settlement?.[language] ?? "",
			getTariff: (item: Order.Model) => item.rate?.name?.[language] ?? "",
			getServices: (item: Order.Model) =>
				item.orderToServices
					?.map((it) => it?.service?.name?.[language] ?? "")
					.filter(Boolean)
					.join(", ") ?? "",
		}),
		[language],
	);

	const getSortAndLimitOrderSubHandler = useCallback(
		(
			{ limit, order, query }: useOrderSubHandler.SmallSubscribeOptions,
			roleSubscribe: Order.Model[],
		): Order.Model[] => {
			if (!Array.isArray(roleSubscribe)) {
				return [];
			}

			let retval = roleSubscribe;

			if (isQuery(query)) {
				const fields = [
					(order: Order.Model) =>
						getGroupStatic.getCustomerFullName(order),
					(order: Order.Model) => getGroupStatic.getPhone(order),
					(order: Order.Model) =>
						getGroupTranslation.getPickupAddress(order),
					(order: Order.Model) =>
						getGroupTranslation.getDestinationAddress(order),
					(order: Order.Model) =>
						getGroupStatic.getOrderNumber(order).toString(),
				];

				retval = retval.filter((order) =>
					fields.some(
						(fn) =>
							isTextMatchingLeadingStarsFilterHelper(
								fn(order),
								query,
							) ||
							isTextMatchingAnySegmentFilterHelper(
								fn(order),
								query,
							),
					),
				);
			}

			if (isOrder(order)) {
				const [[dataKey, sortType] = []] = Object.entries(order || {});
				const isSort =
					typeof sortType === "string" &&
					typeof dataKey === "string" &&
					["asc", "desc"].includes(sortType.toLowerCase());

				const input = retval;
				const prev = prevSortRef?.current;
				const getSortValue = (item: Order.Model) => {
					switch (dataKey) {
						case ColumnId.ExecutorArrivalTime:
						case ColumnId.ExecutorArrivalDate:
							return getGroupStatic.getExecutorArrivalTime(item);
						case ColumnId.TimeToDelivery:
							return getGroupStatic.getTimeToDelivery(item);
						case ColumnId.ExecutorCallSign:
							return getGroupStatic.getExecutorCallSign(item);
						case ColumnId.OrderDate:
						case ColumnId.OrderTime:
							return getGroupStatic.getOrderDate(item);
						case ColumnId.CreateOrderDate:
							return getGroupStatic.getCreateOrderDate(item);
						case ColumnId.CreateOrderTime:
							return getGroupStatic.getCreateOrderTime(item);
						case ColumnId.PickupSettlement:
							return getGroupStatic.getPickupSettlement(item);
						case ColumnId.Phone:
							return getGroupStatic.getPhone(item);
						case ColumnId.OrderPrice:
							return getGroupStatic.getOrderPrice(item);
						case ColumnId.OrderNumber:
							return getGroupStatic.getOrderNumber(item);
						default:
							return typeof dataKey === "string"
								? (item as any)[dataKey]
								: undefined;
					}
				};
				const byId = new Map(input.map((item) => [item.id, item]));
				const isSameSort =
					prev?.dataKey === dataKey && prev?.sortType === sortType;
				const prevIds = prev?.ids;
				const valuesHit =
					isSort &&
					isSameSort &&
					Array.isArray(prevIds) &&
					prevIds.length === input.length &&
					prevIds.every((id, index) => {
						const item = byId.get(id);
						return (
							item != null &&
							prev?.values?.[index] === getSortValue(item)
						);
					});
				// Must match dataKey/sortType — otherwise changing column/type
				// reuses the previous sorted order while items refs stay equal.
				const isHasLastOrder =
					isSort &&
					isSameSort &&
					(sameOrderItems(prev?.input, input) || valuesHit);

				if (isSort && !isHasLastOrder) {
					const isAscending = sortType.toLowerCase() === "asc";
					const sortOrder = isAscending ? 1 : -1;

					retval = [...retval];

					if (dataKey === ColumnId.OrderDate) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getGroupStatic.getOrderDate(prev);
							const nextValue = getGroupStatic.getOrderDate(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.OrderTime) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getGroupStatic.getOrderDate(prev);
							const nextValue = getGroupStatic.getOrderDate(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ExecutorArrivalDate) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getExecutorArrivalTime(prev);
							const nextValue =
								getGroupStatic.getExecutorArrivalTime(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ExecutorArrivalTime) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getExecutorArrivalTime(prev);
							const nextValue =
								getGroupStatic.getExecutorArrivalTime(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.TimeToDelivery) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getTimeToDelivery(prev);
							const nextValue =
								getGroupStatic.getTimeToDelivery(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ExecutorCallSign) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getExecutorCallSign(prev);
							const nextValue =
								getGroupStatic.getExecutorCallSign(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.PickupSettlement) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getPickupSettlement(prev);
							const nextValue =
								getGroupStatic.getPickupSettlement(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.PickupAddress) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getPickupAddress(prev);
							const nextValue =
								getGroupTranslation.getPickupAddress(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.PickupSector) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupLanguage.getPickupSector(prev);
							const nextValue =
								getGroupLanguage.getPickupSector(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Phone) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getGroupStatic.getPhone(prev);
							const nextValue = getGroupStatic.getPhone(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.OrderPrice) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getOrderPrice(prev);
							const nextValue =
								getGroupStatic.getOrderPrice(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.CustomerCallStatus) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getCustomerCallStatus(prev);
							const nextValue =
								getGroupStatic.getCustomerCallStatus(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ExecutorCallStatus) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getExecutorCallStatus(prev);
							const nextValue =
								getGroupStatic.getExecutorCallStatus(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.OrderNumber) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getOrderNumber(prev);
							const nextValue =
								getGroupStatic.getOrderNumber(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ExecutingStageWithClientsTime) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getExecutingStageWithClientsTime(
									prev,
								);
							const nextValue =
								getGroupStatic.getExecutingStageWithClientsTime(
									next,
								);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.CarClass) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupLanguage.getCarClass(prev);
							const nextValue =
								getGroupLanguage.getCarClass(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.DestinationSettlement) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getDestinationSettlement(prev);
							const nextValue =
								getGroupStatic.getDestinationSettlement(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.DestinationAddress) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getDestinationAddress(prev);
							const nextValue =
								getGroupTranslation.getDestinationAddress(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.DestinationSector) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupLanguage.getDestinationSector(prev);
							const nextValue =
								getGroupLanguage.getDestinationSector(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.PaymentStatus) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getPaymentStatus(prev);
							const nextValue =
								getGroupStatic.getPaymentStatus(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.PaymentType) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getPaymentType(prev);
							const nextValue =
								getGroupStatic.getPaymentType(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.DispatcherCallSign) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getDispatcherCallSign(prev);
							const nextValue =
								getGroupTranslation.getDispatcherCallSign(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Status) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getGroupStatic.getStatus(prev);
							const nextValue = getGroupStatic.getStatus(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Source) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getGroupStatic.getSource(prev);
							const nextValue = getGroupStatic.getSource(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.SourceCallSing) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getSourceCallSing(prev);
							const nextValue =
								getGroupStatic.getSourceCallSing(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ExecutingStage) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getExecutingStage(prev);
							const nextValue =
								getGroupStatic.getExecutingStage(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.CloseReason) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getCloseReason(prev);
							const nextValue =
								getGroupStatic.getCloseReason(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.RideNumber) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getRideNumber(prev);
							const nextValue =
								getGroupStatic.getRideNumber(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.TotalRideCount) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getTotalRideCount(prev);
							const nextValue =
								getGroupStatic.getTotalRideCount(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.CreateOrderDate) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getCreateOrderDate(prev);
							const nextValue =
								getGroupStatic.getCreateOrderDate(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.CreateOrderTime) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getCreateOrderTime(prev);
							const nextValue =
								getGroupStatic.getCreateOrderTime(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.CustomerFullName) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getCustomerFullName(prev);
							const nextValue =
								getGroupStatic.getCustomerFullName(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Counterparty) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getCounterparty(prev);
							const nextValue =
								getGroupStatic.getCounterparty(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.CounterpartyCheck) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getCounterpartyCheck(prev);
							const nextValue =
								getGroupStatic.getCounterpartyCheck(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.CounterpartyCheckCard) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getCounterpartyCheckCard(prev);
							const nextValue =
								getGroupStatic.getCounterpartyCheckCard(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ExecutorLate) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getExecutorLate(prev);
							const nextValue =
								getGroupStatic.getExecutorLate(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ExecutingStageAcceptedTime) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getExecutingStageAcceptedTime(
									prev,
								);
							const nextValue =
								getGroupStatic.getExecutingStageAcceptedTime(
									next,
								);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ExecutingStageArrivedTime) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getExecutingStageArrivedTime(
									prev,
								);
							const nextValue =
								getGroupStatic.getExecutingStageArrivedTime(
									next,
								);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (
						dataKey === ColumnId.ExecutingStageWaitingCustomerTime
					) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupStatic.getExecutingStageWaitingCustomerTime(
									prev,
								);
							const nextValue =
								getGroupStatic.getExecutingStageWaitingCustomerTime(
									next,
								);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.SMSLateStatus) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getSMSLateStatus(prev);
							const nextValue =
								getGroupTranslation.getSMSLateStatus(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.SMSWillBeAtStatus) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getSMSWillBeAtStatus(prev);
							const nextValue =
								getGroupTranslation.getSMSWillBeAtStatus(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.SMSCarNotFoundStatus) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getSMSCarNotFoundStatus(
									prev,
								);
							const nextValue =
								getGroupTranslation.getSMSCarNotFoundStatus(
									next,
								);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.SMSAwaitingPassengersStatus) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getSMSAwaitingPassengersStatus(
									prev,
								);
							const nextValue =
								getGroupTranslation.getSMSAwaitingPassengersStatus(
									next,
								);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.SMSWithPassengersStatus) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getSMSWithPassengersStatus(
									prev,
								);
							const nextValue =
								getGroupTranslation.getSMSWithPassengersStatus(
									next,
								);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.SMSOrderCompletedStatus) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getSMSOrderCompletedStatus(
									prev,
								);
							const nextValue =
								getGroupTranslation.getSMSOrderCompletedStatus(
									next,
								);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.SMSExecutorSwitchedStatus) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getSMSExecutorSwitchedStatus(
									prev,
								);
							const nextValue =
								getGroupTranslation.getSMSExecutorSwitchedStatus(
									next,
								);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Company) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getGroupLanguage.getCompany(prev);
							const nextValue = getGroupLanguage.getCompany(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.TaxiService) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupLanguage.getTaxiService(prev);
							const nextValue =
								getGroupLanguage.getTaxiService(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.IdleTime) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getGroupStatic.getIdleTime(prev);
							const nextValue = getGroupStatic.getIdleTime(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}
					if (dataKey === ColumnId.Tariff) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getGroupLanguage.getTariff(prev);
							const nextValue = getGroupLanguage.getTariff(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Services) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupLanguage.getServices(prev);
							const nextValue =
								getGroupLanguage.getServices(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Parameters) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getParameters(prev);
							const nextValue =
								getGroupTranslation.getParameters(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Discount) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getGroupStatic.getDiscount(prev);
							const nextValue = getGroupStatic.getDiscount(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.TransportationType) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								getGroupTranslation.getTransportationType(prev);
							const nextValue =
								getGroupTranslation.getTransportationType(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					prevSortRef.current = {
						dataKey,
						sortType,
						input,
						sorted: retval,
						ids: retval.map((item) => item.id),
						values: retval.map(getSortValue),
					};
				} else if (isHasLastOrder && prevSortRef?.current) {
					if (
						sameOrderItems(prevSortRef.current.input, input) &&
						prevSortRef.current.sorted
					) {
						retval = prevSortRef.current.sorted;
					} else if (prevSortRef.current.ids) {
						retval = prevSortRef.current.ids
							.map((id) => byId.get(id))
							.filter(Boolean) as Order.Model[];
					}
				}
			}

			if (isLimit(limit)) {
				retval = retval.slice(0, limit);
			}

			return retval;
		},
		[getGroupLanguage, getGroupStatic, getGroupTranslation],
	);

	return { getSortAndLimitOrderSubHandler };
};

declare namespace useOrderSubHandler {
	interface Props {
		getSortAndLimitOrderSubHandler: (
			data: SmallSubscribeOptions,
			roleSubscribe: Order.Model[],
		) => Order.Model[];
	}

	interface SmallSubscribeOptions {
		order?: Order.Order;
		limit?: number;
		query?: string;
	}
}

export default useOrderSubHandler;
